import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import styles from './AddStudentToSession.module.sass';
import Card from '../../../../components/Card';
import StudentSearchBar from '../../../../components/StudentSearchBar';
import Icon from '../../../../components/Icon';
import LetterAvatar from '../../../../components/LetterAvatar';
import { createAttendance, getAttendancesBySessionId, deleteAttendance } from '../../../../services/attendances.service';
import { showSuccess, showError } from '../../../../services/toast.service';

const AddStudentToSession = ({ session, onStudentAdded, onSubmit, loading: formLoading, isDetailMode = true }) => {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [sessionAttendances, setSessionAttendances] = useState([]);
    const [loadingAttendances, setLoadingAttendances] = useState(true);
    const [addingStudent, setAddingStudent] = useState(false);

    // Charger les attendances existantes de la session
    const fetchAttendances = useCallback(async () => {
        if (!session?.id) return;
        
        setLoadingAttendances(true);
        try {
            const attendances = await getAttendancesBySessionId(session.id);
            setSessionAttendances(attendances || []);
        } catch (error) {
            console.error('Erreur lors du chargement des attendances:', error);
        } finally {
            setLoadingAttendances(false);
        }
    }, [session?.id]);

    useEffect(() => {
        fetchAttendances();
    }, [fetchAttendances]);

    // Vérifier si l'élève est déjà dans la session
    const isStudentAlreadyInSession = (studentId) => {
        return sessionAttendances.some(att => att.participant_id === studentId);
    };

    // Gérer la sélection d'un étudiant
    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
    };

    // Ajouter l'élève à la session
    const handleAddStudent = async () => {
        if (!selectedStudent || !session?.id) {
            showError('Veuillez sélectionner un élève');
            return;
        }

        // Vérifier si l'élève est déjà dans la session
        if (isStudentAlreadyInSession(selectedStudent.id)) {
            showError('Cet élève est déjà inscrit dans cette session');
            return;
        }

        setAddingStudent(true);
        try {
            const attendanceData = {
                session_id: session.id,
                participant_id: selectedStudent.id,
                partner_id: session.partner_id,
                status: 'Absent',
                method: 'manual' // Pour identifier les ajouts manuels
            };

            await createAttendance(attendanceData);
            
            showSuccess(`${selectedStudent.first_name} ${selectedStudent.last_name} a été ajouté(e) à la session`);
            
            // Réinitialiser la sélection
            setSelectedStudent(null);
            
            // Recharger les attendances
            await fetchAttendances();
            
            // Notifier le parent
            if (onStudentAdded) {
                onStudentAdded();
            }
        } catch (error) {
            console.error('Erreur lors de l\'ajout de l\'élève:', error);
            showError('Erreur lors de l\'ajout de l\'élève à la session');
        } finally {
            setAddingStudent(false);
        }
    };

    // Supprimer un élève de la session
    const handleRemoveStudent = async (attendanceId, studentName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${studentName} de cette session ?`)) {
            return;
        }

        try {
            await deleteAttendance(attendanceId);
            showSuccess(`${studentName} a été retiré(e) de la session`);
            
            // Recharger les attendances
            await fetchAttendances();
            
            // Notifier le parent
            if (onStudentAdded) {
                onStudentAdded();
            }
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            showError('Erreur lors de la suppression de l\'élève de la session');
        }
    };

    return (
        <Card 
            className={styles.card} 
            title="Gestion des participants"
            classTitle="title-green"
        >
            {/* Section d'ajout d'élève */}
            <div className={styles.addSection}>
                
                
                <div className={styles.searchContainer}>
                    <StudentSearchBar
                        className={styles.searchBar}
                        label="Recherchez et ajoutez un élève manuellement à cette session."
                        placeholder="Nom, email ou téléphone"
                        onSelectStudent={handleSelectStudent}
                        showDetails={false}
                    />
                </div>

                {selectedStudent && (
                    <div className={styles.selectedStudent}>
                        <div className={styles.studentPreview}>
                            <LetterAvatar
                                firstName={selectedStudent.first_name}
                                lastName={selectedStudent.last_name}
                                size={40}
                            />
                            <div className={styles.studentInfo}>
                                <div className={styles.studentName}>
                                    {selectedStudent.first_name} {selectedStudent.last_name}
                                </div>
                                <div className={styles.studentEmail}>
                                    {selectedStudent.email}
                                </div>
                            </div>
                        </div>
                        
                        <button
                            className={cn("button", styles.addButton)}
                            onClick={handleAddStudent}
                            disabled={addingStudent || isStudentAlreadyInSession(selectedStudent.id)}
                        >
                            {addingStudent ? (
                                <>
                                    <span className={styles.spinner}></span>
                                    Ajout en cours...
                                </>
                            ) : (
                                <>
                                    <Icon name="plus" size="16" />
                                    Ajouter à la session
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.divider}></div>

            {/* Liste des élèves de la session */}
            <div className={styles.participantsSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>
                        Élèves inscrits à la session
                    </div>
                    <div className={styles.participantCount}>
                        {sessionAttendances.length} élève{sessionAttendances.length > 1 ? 's' : ''}
                    </div>
                </div>

                {loadingAttendances ? (
                    <div className={styles.loading}>Chargement des participants...</div>
                ) : sessionAttendances.length > 0 ? (
                    <div className={styles.participantsList}>
                        {sessionAttendances.map((attendance) => {
                            const participant = attendance.expand?.participant_id;
                            if (!participant) return null;
                            
                            return (
                                <div key={attendance.id} className={styles.participantCard}>
                                    <div className={styles.participantInfo}>
                                        <LetterAvatar
                                            firstName={participant.first_name}
                                            lastName={participant.last_name}
                                            size={36}
                                        />
                                        <div className={styles.participantDetails}>
                                            <div className={styles.participantName}>
                                                {participant.first_name} {participant.last_name}
                                            </div>
                                            <div className={styles.participantEmail}>
                                                {participant.email}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.participantActions}>
                                        <button
                                            className={styles.removeButton}
                                            onClick={() => handleRemoveStudent(
                                                attendance.id, 
                                                `${participant.first_name} ${participant.last_name}`
                                            )}
                                            title="Retirer de la session"
                                        >
                                            <Icon name="trash" size="18" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>Aucun élève inscrit dans cette session</p>
                        <span>Utilisez la recherche ci-dessus pour ajouter des élèves</span>
                    </div>
                )}
            </div>

            <div className={styles.divider}></div>

            {/* Bouton de mise à jour de la session */}
            <div className={styles.actions}>
                <button
                    className={cn("button", styles.button)}
                    type="button"
                    onClick={onSubmit}
                    disabled={formLoading}
                >
                    {formLoading ? "Mise à jour en cours..." : "Mettre à jour la session"}
                </button>
            </div>
        </Card>
    );
};

export default AddStudentToSession;
